/**
 * BudgetVsActual — Side-by-side budget vs actual with variance indicators.
 *
 * Requirements: 45.4, 45.5, 45.6, 45.8, 45.11, 45.12
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  budgetService,
  BudgetVsActualResponse,
  BudgetVsActualLineItem,
} from '@/services/budgetService';
import type { Budget } from '@/types';

const STATUS_COLORS: Record<string, string> = {
  green: 'bg-green-100 text-green-800',
  yellow: 'bg-yellow-100 text-yellow-800',
  red: 'bg-red-100 text-red-800',
};

const STATUS_LABELS: Record<string, string> = {
  green: 'Under Budget',
  yellow: 'Near Budget',
  red: 'Over Budget',
};

function formatCurrency(amount: number): string {
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatPercent(value: number | null): string {
  if (value === null) return '—';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
}

export function BudgetVsActual() {
  const { id } = useParams<{ id: string }>();
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [selectedBudgetId, setSelectedBudgetId] = useState<number | null>(
    id ? Number(id) : null,
  );
  const [result, setResult] = useState<BudgetVsActualResponse | null>(null);
  const [glCodeFilter, setGlCodeFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    budgetService.list().then(setBudgets).catch(() => {});
  }, []);

  const loadComparison = useCallback(async () => {
    if (!selectedBudgetId) return;
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number | undefined> = {};
      if (glCodeFilter) params.glCode = glCodeFilter;
      if (categoryFilter) params.budgetCategory = categoryFilter;

      const data = await budgetService.getVsActual(selectedBudgetId, params);
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comparison.');
    } finally {
      setLoading(false);
    }
  }, [selectedBudgetId, glCodeFilter, categoryFilter]);

  useEffect(() => { loadComparison(); }, [loadComparison]);

  return (
    <div >
      <h1 className="text-lg font-semibold mb-6">Budget vs. Actual</h1>

      <div className="flex gap-4 mb-6 items-end">
        <div>
          <label htmlFor="budgetSelect" className="block text-sm font-medium mb-1">Budget</label>
          <select
            id="budgetSelect"
            value={selectedBudgetId ?? ''}
            onChange={e => setSelectedBudgetId(Number(e.target.value) || null)}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
          >
            <option value="">Select a budget...</option>
            {budgets.map(b => (
              <option key={b.budgetId} value={b.budgetId}>
                FY{b.fiscalYear} — {b.budgetName ?? 'Untitled'}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="glFilter" className="block text-sm font-medium mb-1">GL Code</label>
          <input
            id="glFilter"
            type="text"
            value={glCodeFilter}
            onChange={e => setGlCodeFilter(e.target.value)}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
            placeholder="Filter by GL code"
          />
        </div>
        <div>
          <label htmlFor="catFilter" className="block text-sm font-medium mb-1">Category</label>
          <input
            id="catFilter"
            type="text"
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
            placeholder="Filter by category"
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded" role="alert">
          {error}
        </div>
      )}

      {loading && <div className="text-slate-400">Loading comparison...</div>}

      {result && !loading && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <SummaryCard label="Total Budgeted" value={formatCurrency(result.totalBudgeted)} />
            <SummaryCard label="Total Actual" value={formatCurrency(result.totalActual)} />
            <SummaryCard
              label="Variance ($)"
              value={formatCurrency(result.totalDollarVariance)}
              color={result.totalDollarVariance >= 0 ? 'text-green-600' : 'text-red-600'}
            />
            <SummaryCard
              label="Variance (%)"
              value={formatPercent(result.totalPercentVariance)}
              color={
                result.totalPercentVariance !== null && result.totalPercentVariance >= 0
                  ? 'text-green-600' : 'text-red-600'
              }
            />
          </div>

          {/* Detail table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-page">
                  <th className="border px-3 py-2 text-left">GL Code</th>
                  <th className="border px-3 py-2 text-left">Description</th>
                  <th className="border px-3 py-2 text-left">Category</th>
                  <th className="border px-3 py-2 text-right">Budgeted</th>
                  <th className="border px-3 py-2 text-right">Actual</th>
                  <th className="border px-3 py-2 text-right">Variance ($)</th>
                  <th className="border px-3 py-2 text-right">Variance (%)</th>
                  <th className="border px-3 py-2 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {result.lineItems.map((li: BudgetVsActualLineItem) => (
                  <tr key={li.lineItemId} className="hover:bg-page">
                    <td className="border px-3 py-2">{li.glCode ?? '—'}</td>
                    <td className="border px-3 py-2">{li.description ?? '—'}</td>
                    <td className="border px-3 py-2">{li.budgetCategory ?? '—'}</td>
                    <td className="border px-3 py-2 text-right">{formatCurrency(li.budgetedAmount)}</td>
                    <td className="border px-3 py-2 text-right">{formatCurrency(li.actualAmount)}</td>
                    <td className={`border px-3 py-2 text-right ${li.dollarVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(li.dollarVariance)}
                    </td>
                    <td className={`border px-3 py-2 text-right ${li.percentVariance !== null && li.percentVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatPercent(li.percentVariance)}
                    </td>
                    <td className="border px-3 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[li.status]}`}>
                        {STATUS_LABELS[li.status]}
                      </span>
                    </td>
                  </tr>
                ))}
                {result.lineItems.length === 0 && (
                  <tr>
                    <td colSpan={8} className="border px-4 py-8 text-center text-slate-500">
                      No budget line items found for the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!selectedBudgetId && !loading && (
        <div className="text-slate-500 text-center py-12">
          Select a budget to view the variance analysis.
        </div>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <div className="bg-white bg-white rounded-lg border border-border p-5">
      <div className="text-sm text-slate-500 mb-1">{label}</div>
      <div className={`text-xl font-semibold ${color ?? 'text-gray-900'}`}>{value}</div>
    </div>
  );
}
